import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Instagram, Facebook, Youtube, Loader2, Send, Trash2, Clock, Image as ImageIcon, X, Info, Link2, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const PLATFORM_META = {
  instagram: { label: "Instagram", Icon: Instagram, color: "#C13584" },
  facebook: { label: "Facebook", Icon: Facebook, color: "#1877F2" },
  youtube: { label: "YouTube", Icon: Youtube, color: "#FF0000" },
};

export default function SocialPanel() {
  const qc = useQueryClient();
  const imgInput = useRef(null);
  const [caption, setCaption] = useState("");
  const [media, setMedia] = useState([]);
  const [targets, setTargets] = useState([]);
  const [scheduledAt, setScheduledAt] = useState("");
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);

  const { data: connections = [] } = useQuery({ queryKey: ["social-connections"], queryFn: () => api.get("/enhancer/social/connections").then((r) => r.data) });
  const { data: posts = [] } = useQuery({ queryKey: ["social-posts"], queryFn: () => api.get("/enhancer/social/posts").then((r) => r.data) });

  const toggleTarget = (p) => setTargets((t) => (t.includes(p) ? t.filter((x) => x !== p) : [...t, p]));

  const connect = async (platform) => {
    const handle = window.prompt(`Enter your ${PLATFORM_META[platform].label} handle or profile URL`);
    if (handle === null) return;
    try { await api.post("/enhancer/social/connections", { platform, handle }); toast.success(`${PLATFORM_META[platform].label} saved`); qc.invalidateQueries({ queryKey: ["social-connections"] }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const disconnect = async (platform) => {
    try { await api.delete(`/enhancer/social/connections/${platform}`); qc.invalidateQueries({ queryKey: ["social-connections"] }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onImages = async (e) => {
    if (!e.target.files?.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(e.target.files)) {
        const fd = new FormData(); fd.append("file", file);
        const r = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setMedia((m) => [...m, r.data.url].slice(0, 8));
      }
    } catch (err) { toast.error("We couldn't upload that file. Please check it and try again."); } finally { setUploading(false); }
  };

  const submit = async () => {
    if (targets.length === 0) return toast.error("Choose at least one platform.");
    if (!caption.trim() && media.length === 0) return toast.error("Add a caption or an image.");
    setPosting(true);
    try {
      await api.post("/enhancer/social/posts", { caption, media, platforms: targets, scheduled_at: scheduledAt });
      toast.success(scheduledAt ? "Post scheduled" : "Post queued");
      setCaption(""); setMedia([]); setTargets([]); setScheduledAt("");
      qc.invalidateQueries({ queryKey: ["social-posts"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setPosting(false); }
  };
  const del = async (id) => {
    try { await api.delete(`/enhancer/social/posts/${id}`); qc.invalidateQueries({ queryKey: ["social-posts"] }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-3xl" data-testid="enh-social-panel">
      <h2 className="font-serif text-2xl text-[#1A242B] mb-1">Social cross-posting</h2>
      <p className="text-[#4A5D6B] text-sm mb-4">Connect your accounts and compose posts once — share to Instagram, Facebook and YouTube from here.</p>

      <div className="rounded-xl bg-[#144F75]/[0.06] border border-[#144F75]/20 p-3 mb-6 flex gap-2 text-sm text-[#144F75]" data-testid="social-foundation-note">
        <Info size={16} className="shrink-0 mt-0.5" />
        <span>Live publishing turns on once Enhancing Your World completes platform setup &amp; app review. Until then, your connected accounts and composed posts are saved here, ready to publish.</span>
      </div>

      {/* Connect accounts */}
      <div className="eyw-card p-5 mb-6" data-testid="social-connections">
        <p className="text-sm font-semibold text-[#1A242B] mb-3 flex items-center gap-2"><Link2 size={16} /> Connected accounts</p>
        <div className="grid sm:grid-cols-3 gap-3">
          {connections.map((c) => {
            const m = PLATFORM_META[c.platform]; const Icon = m.Icon;
            return (
              <div key={c.platform} className="rounded-xl border border-[#E5E0D8] p-3" data-testid={`social-conn-${c.platform}`}>
                <div className="flex items-center gap-2 text-[#1A242B] font-medium text-sm"><Icon size={16} style={{ color: m.color }} /> {m.label}</div>
                {c.connected ? (
                  <>
                    <p className="text-xs text-[#4A5D6B] mt-1 truncate">{c.handle || "—"}</p>
                    <p className="text-[11px] text-[#B5794E] mt-0.5">Pending setup</p>
                    <button onClick={() => disconnect(c.platform)} data-testid={`social-disconnect-${c.platform}`} className="text-xs text-red-500 hover:underline mt-1">Remove</button>
                  </>
                ) : (
                  <button onClick={() => connect(c.platform)} data-testid={`social-connect-${c.platform}`} className="mt-2 text-xs eyw-btn !py-1.5 !px-3">Connect</button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Composer */}
      <div className="eyw-card p-5 mb-6" data-testid="social-composer">
        <p className="text-sm font-semibold text-[#1A242B] mb-3">Compose a post</p>
        <textarea rows={4} value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="social-caption" className="eyw-input w-full resize-none" placeholder="Write your caption…" />
        <div className="flex flex-wrap gap-3 mt-3">
          {media.map((url, i) => (
            <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-[#E5E0D8]" data-testid={`social-media-${i}`}>
              <img src={url} alt="" className="w-full h-full object-cover" />
              <button onClick={() => setMedia(media.filter((_, x) => x !== i))} data-testid={`social-media-remove-${i}`} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5"><X size={11} /></button>
            </div>
          ))}
          <button onClick={() => imgInput.current?.click()} disabled={uploading} data-testid="social-add-media" className="w-20 h-20 rounded-lg border-2 border-dashed border-[#C9BFA8] grid place-items-center text-[#8FA89B] hover:border-[#144F75] hover:text-[#144F75]">
            {uploading ? <Loader2 className="animate-spin" size={18} /> : <ImageIcon size={20} />}
          </button>
          <input ref={imgInput} type="file" accept="image/*" multiple hidden onChange={onImages} />
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <span className="text-sm text-[#4A5D6B]">Post to:</span>
          {Object.entries(PLATFORM_META).map(([p, m]) => {
            const Icon = m.Icon; const active = targets.includes(p);
            return (
              <button key={p} onClick={() => toggleTarget(p)} data-testid={`social-target-${p}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors ${active ? "text-white border-transparent" : "bg-white text-[#4A5D6B] border-[#E5E0D8]"}`}
                style={active ? { backgroundColor: m.color } : {}}>
                <Icon size={14} /> {m.label}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-4">
          <label className="text-sm text-[#4A5D6B] flex items-center gap-2"><Clock size={14} /> Schedule (optional)</label>
          <input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} data-testid="social-schedule" className="eyw-input !py-1.5 text-sm" />
          <button onClick={submit} disabled={posting} data-testid="social-submit" className="eyw-btn ml-auto disabled:opacity-60">
            {posting ? <Loader2 className="animate-spin" size={16} /> : <Send size={15} />} {scheduledAt ? "Schedule post" : "Save post"}
          </button>
        </div>
      </div>

      {/* Post list */}
      <div data-testid="social-posts-list">
        <p className="text-sm font-semibold text-[#1A242B] mb-2">Your posts</p>
        {posts.length === 0 ? (
          <p className="text-sm text-[#8FA89B]" data-testid="social-posts-empty">No posts yet. Compose one above.</p>
        ) : (
          <ul className="space-y-3">
            {posts.map((p) => (
              <li key={p.id} className="eyw-card p-4 flex items-start justify-between gap-3" data-testid={`social-post-${p.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {p.platforms.map((pl) => { const M = PLATFORM_META[pl]; const I = M.Icon; return <I key={pl} size={14} style={{ color: M.color }} />; })}
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#D98E73]/15 text-[#B5794E] inline-flex items-center gap-1">
                      {p.scheduled_at ? <><Clock size={10} /> {String(p.scheduled_at).replace("T", " ")}</> : "Queued"}
                    </span>
                    <span className="text-[11px] text-[#8FA89B]">· awaiting publishing</span>
                  </div>
                  {p.caption && <p className="text-sm text-[#1A242B] line-clamp-2">{p.caption}</p>}
                  {p.media?.length > 0 && (
                    <div className="flex gap-1.5 mt-2">{p.media.slice(0, 5).map((u, i) => <img key={i} src={u} alt="" className="w-10 h-10 rounded object-cover border border-[#E5E0D8]" />)}</div>
                  )}
                </div>
                <button onClick={() => del(p.id)} data-testid={`social-post-delete-${p.id}`} className="shrink-0 text-[#B45151] hover:bg-[#B45151]/10 rounded-lg p-2"><Trash2 size={15} /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
